import * as env from '@/common/environment';
import {
  buildConnectConfig,
  canConnectToAgent,
  checkAiABTestingPassed,
  checkAiTransactionABTestingPassed,
  EventType,
  mergeConnectConfig,
} from '../../lib';
import { IsUserEligibleForAiFlowParams, UserId } from './AiService.types';
import { MINUTE } from '@/common/date';
import { BaseEvent, ConnectToAgent } from '../../types';
import { InitialConnectAiEvent } from '../ChatFramework.types';

export const getRecentlyConnectedAiKey = (userId: UserId): string => `AI-${userId}-connect`;

export const hasRecentlyConnectedAI = (userId: UserId, conversationCooldownMins: number | null = 0): boolean => {
  if (!userId || !conversationCooldownMins) {
    return false;
  }

  const { bypassAbTestingUserIds }: { bypassAbTestingUserIds?: string[] } = env.get('AI') || {
    bypassAbTestingUserIds: [],
  };

  // Specified users should see AI without any restrictions
  if (bypassAbTestingUserIds?.includes(String(userId))) {
    return false;
  }

  // Time in milliseconds or null value
  const recentlyConnectedValue = localStorage.getItem(getRecentlyConnectedAiKey(userId));
  if (!recentlyConnectedValue) return false;

  const recentlyConnectedTimestamp = Number(recentlyConnectedValue);
  const conversationCooldownMs = conversationCooldownMins * MINUTE;

  return Date.now() - recentlyConnectedTimestamp < conversationCooldownMs;
};

export const setRecentlyConnectedAI = (userId?: UserId): void => {
  if (!userId) {
    return;
  }

  localStorage.setItem(getRecentlyConnectedAiKey(userId), String(Date.now()));
};

const BYPASS_AI_FLOW_LOYALTY_TIERS = ['platinum', 'diamond'];

const checkBypassAiFlowLoyaltyTier = (userId: UserId, userLoyaltyTier?: string): boolean => {
  if (!userId || !userLoyaltyTier) {
    return false;
  }

  const { bypassAbTestingUserIds }: { bypassAbTestingUserIds?: string[] } = env.get('AI') || {
    bypassAbTestingUserIds: [],
  };

  const bypassAbTesting = bypassAbTestingUserIds?.includes(String(userId));

  // For bypass testing users we don't need to hide AI flow
  if (bypassAbTesting) {
    return false;
  }

  const loyaltyTier = userLoyaltyTier.toLowerCase();
  return BYPASS_AI_FLOW_LOYALTY_TIERS.includes(loyaltyTier);
};

export const isUserEligibleForAiFlow = (params: IsUserEligibleForAiFlowParams): boolean => {
  const {
    userId,
    userLoyaltyTier,
    skipAiChat = false,
    aiEnabledPerBrand = false,
    aiEnabledPerSession = false,
    enableMetadataRules = false,
    openAiDown = false,
    abTestPercentage = 0,
    aiAbTestingEnabled = false,
    aiEndUserComeBackTime = 0,
    aiTransactionEnabled = false,
    aiTransactionAbRatio = 0,
  } = params;

  const aiAbTestPassed = checkAiABTestingPassed({
    userId,
    abValue: abTestPercentage,
    enableAbTesting: aiAbTestingEnabled,
  });

  const aiTransactionAbTestPassed = checkAiTransactionABTestingPassed({
    userId,
    enableAbTesting: aiTransactionEnabled,
    abValue: aiTransactionAbRatio,
    aiAbTestPassed,
  });

  const didUserRecentlyConnectToAI = hasRecentlyConnectedAI(userId, aiEndUserComeBackTime);
  const userBypassAiFlowLoyaltyTier = checkBypassAiFlowLoyaltyTier(userId, userLoyaltyTier);
  // If AI Transaction is enabled, we won't show Metadata Automation Flow
  const metadataAutomationCheck = !enableMetadataRules || aiTransactionAbTestPassed;

  return Boolean(
    aiEnabledPerBrand &&
    aiEnabledPerSession &&
    aiAbTestPassed &&
    metadataAutomationCheck &&
    !openAiDown &&
    !skipAiChat &&
    !didUserRecentlyConnectToAI &&
    !userBypassAiFlowLoyaltyTier
  );
};

export const getAiEventConfig = (event: InitialConnectAiEvent) => {
  const isExternalEvent = EventType.isExternalEvent(event);
  const connectConfig = (isExternalEvent
    ? event.payload.supportOptions
    : (event as BaseEvent).message.config?.connect) as ConnectToAgent;

  return connectConfig;
};

// Here we create connect config for finish flow based on the ability to connect to agent.
// If we can connect to agent, we'll show existing options, otherwise we'll all hardcoded options.
export const getAiFinishFlowConfig = (connectConfig: ConnectToAgent) => {
  const ableToConnectToAgent = canConnectToAgent(connectConfig);
  const hardcodedAllowConnectConfig = buildConnectConfig({
    connectToAgent: true,
    loginRequired: true,
    showLiveChat: true,
    showVoiceChat: true,
    showOpenRequest: true,
  });

  return ableToConnectToAgent ? connectConfig : mergeConnectConfig(connectConfig, hardcodedAllowConnectConfig);
};

export const getAiEventFinishFlowConfig = (event: InitialConnectAiEvent) => {
  const connectConfig = getAiEventConfig(event);
  return getAiFinishFlowConfig(connectConfig);
};
